import { GetServerSideProps } from 'next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import Accordion from 'react-bootstrap/Accordion'

import CardHover from '@/components/CardHover/CardHover'
import Container from '@/components/Container/Container'
import { ExplicitHead } from '@/components/DefaultHead/DefaultHead'
import Main from '@/components/Main/Main'
import Mana from '@/components/Mana/Mana'
import OutboundLink from '@/components/OutboundLink/OutboundLink'
import Panel from '@/components/Panel/Panel'
import SimpleHeader from '@/components/SimpleHeader/SimpleHeader'
import { userIsBanned } from '@/utils/helpers.server'

const dateHelper = (dateString: string) =>
  new Intl.DateTimeFormat(undefined, { dateStyle: 'long', timeZone: 'UTC' }).format(Date.parse(dateString))

const DATA = [
  {
    body: (
      <>
        EDHREC is a deck analysis tool for the Commander format (a.k.a. EDH) of Magic: The Gathering. EDHREC computes
        decklist statistics from popular deck-building websites, helping players new and old build decks of their own.
        <br />
        <br />
        Not sure where to get started? Read{' '}
        <OutboundLink
          url='https://edhrec.com/articles/digital-deckbuilding-the-how-to-guide-to-building-a-commander-deck-using-edhrec-archidekt-and-commander-spellbook'
          utm_medium='faq'
        >
          Digital Deckbuilding - The How-to Guide to Building a Commander Deck Using EDHREC, Archidekt and Commander
          Spellbook
        </OutboundLink>
        .
      </>
    ),
    header: 'Help! What is EDHREC?',
  },
  {
    body: (
      <>
        <p>
          Stats such as <em>inclusion</em> and <em>lift</em> measure how often <strong>two cards</strong> are run
          together. This usually refers to <strong>(1)</strong> the featured card at the top of the page, and{' '}
          <strong>(2)</strong> the card being analyzed in relation to it.
        </p>
        <p>
          When calculating these stats, EDHREC compares &quot;number of decks that include both cards&quot; to
          &quot;number of decks that <i>could have</i> included both cards.&quot; We call the latter stat{' '}
          <strong>potential decks</strong>.
        </p>
        <p>
          To count as a <strong>potential deck</strong>, a deck must be able to run both cards. This considers
          deck-building restrictions including commander, color identity, and the dates when both cards were first
          previewed. In other words, decks that can&apos;t run both cards due to color identity restrictions don&apos;t
          count, nor do decks built prior to both cards existing.
        </p>
        <p>
          <strong>Potential decks</strong> also follows the current active filters. For example, if you are viewing
          Yuriko with Budget set to Expensive, Bracket set to 4, and Tag set to Ninjas, the potential decks will also be
          narrowed by those same filters.
        </p>
        <h5 className='mb-2'>
          Example 1: <CardHover name='Moon-Circuit Hacker'>Moon-Circuit Hacker</CardHover> in{' '}
          <CardHover name="Yuriko, the Tiger's Shadow">Yuriko, the Tiger&apos;s Shadow</CardHover> decks
        </h5>
        <ul>
          <li>
            <CardHover name="Yuriko, the Tiger's Shadow">Yuriko</CardHover> is a <Mana colors='ub' /> commander,
            previewed on {dateHelper('2018-08-08')}
          </li>
          <li>
            <CardHover name='Moon-Circuit Hacker'>Moon-Circuit Hacker</CardHover> is <Mana color='u' />, previewed on{' '}
            {dateHelper('2022-02-18')}
          </li>
        </ul>
        <p>
          In this case, <em>decks</em> refers to every <CardHover name="Yuriko, the Tiger's Shadow">Yuriko</CardHover>{' '}
          deck running <CardHover name='Moon-Circuit Hacker'>Moon-Circuit Hacker</CardHover>, and{' '}
          <strong>potential decks</strong> refers to every{' '}
          <CardHover name="Yuriko, the Tiger's Shadow">Yuriko</CardHover> deck updated on or after{' '}
          {dateHelper('2022-02-18')}.
        </p>
        <h5 className='mb-2'>
          Example 2: <CardHover name='Cultivate'>Cultivate</CardHover> and{' '}
          <CardHover name="Assassin's Trophy">Assassin&apos;s Trophy</CardHover>
        </h5>
        <ul>
          <li>
            <CardHover name='Cultivate'>Cultivate</CardHover> is <Mana color='g' />, previewed on{' '}
            {dateHelper('2010-06-16')}
          </li>
          <li>
            <CardHover name="Assassin's Trophy">Assassin&apos;s Trophy</CardHover> is <Mana colors='bg' />, previewed on{' '}
            {dateHelper('2018-10-05')}
          </li>
        </ul>
        <p className='mb-0'>
          In this case, <em>decks</em> refers to every deck running <CardHover name='Cultivate'>Cultivate</CardHover>{' '}
          and <CardHover name="Assassin's Trophy">Assassin&apos;s Trophy</CardHover>, and{' '}
          <strong>potential decks</strong> refers to decks updated on or after {dateHelper('2018-10-05')} with{' '}
          <em>at least</em> <Mana colors='bg' /> in their color identity.
        </p>
      </>
    ),
    header: 'What are "Potential Decks"?',
  },
  {
    body: (
      <>
        EDHREC collects deck data from Archidekt, Moxfield, and Scryfall. We collect data daily from these pages and new
        data is reflected most often on pages within a few days. Tappedout was a source of data in the early stages of
        the website so some decks may still appear, but we are not collecting data from them currently. <br /> <br />
        If your deck is on one of those deck building sites, chances are it’ll get pulled in. However, there are a
        number of things that can prevent a deck from being pulled in. Most notably, make sure it’s a legal deck. If you
        have too many cards, too few cards, cards out of your color identity, or banned cards, our database will not
        pick it up intentionally. <br /> <br />
        However, there are a number of things that can go wrong between EDHREC and the source websites and there is no
        way to notify you that something went wrong. Sometimes they may not be your fault, or anyone’s fault. We simply
        can’t guarantee that every deck makes it up on EDHREC. <br /> <br />
        If you update your deck on the deck building site, EDHREC will collect it as a new deck and delete our old one,
        effectively updating the deck in our database. Updating your deck a bunch of times does not make duplicates of
        the deck in our database.
      </>
    ),
    header:
      'Where does EDHREC collect its deck data from? How can I get my deck on EDHREC? What happens when I update my deck?',
  },
  {
    body: (
      <>
        EDHREC does not under any circumstances approve of or publish AI-generated articles. This includes but is not
        limited to ChatGPT, Google Gemini, and other Large Language Models (LLMs) and content generation tools,
        platforms, and program applications.
      </>
    ),
    header: 'Are EDHREC articles written by AI?',
  },
  {
    body: (
      <>
        On the{' '}
        <Link href='/themes' target='_blank'>
          themes page
        </Link>
        , and the{' '}
        <Link href='/typal' target='_blank'>
          typal page
        </Link>
        , there are dozens of different themes listed. How these are actually defined are not scientific and are simply
        the developers of EDHREC fiddling with some counts until it appears that the page provides decent results. Each
        theme also is defined individually and we don’t have one defined formula that covers all themes. <br /> <br />
        As such, we honestly don’t have that good of an answer to this question. We hope the end resulting pages provide
        some value to people trying to build those themes, but there are plenty of decks that simply didn’t meet the
        definition but probably could be considered that from a human perspective.
      </>
    ),
    header: 'How are themes defined? How is typal defined?',
  },
  {
    body: (
      <>
        Salt Scores are based on an annual survey of commander players. Users were showed a card and asked on a scale of
        0-4 how salty that card made them. We collected the results and now have{' '}
        <Link href='/top/salt' target='_blank'>
          a top list of the saltiest cards
        </Link>{' '}
        and also the score appears on cards with a high enough salt score. <br /> <br />
        Read{' '}
        <a href='https://edhrec.com/articles/new-edhrec-feature-salt/' rel='noreferrer' target='_blank'>
          this article
        </a>{' '}
        for more information.
      </>
    ),
    header: 'What is the Salt Score?',
  },
  {
    body: (
      <>
        We can run custom ad campaigns for companies and individuals that want to target EDHREC users.{' '}
        <Link href='/contact-us' target='_blank'>
          Contact us
        </Link>{' '}
        and we can send over the ad units we have available and some more information on what we can do. <br /> <br />
        In general, we will only advertise products that we as EDHREC can stand behind in terms of quality and value. If
        your product is well known we might be to able to determine the reputation publicly, but if not we may request
        some samples (we can pay for them if the campaign is large enough) to review them for ourselves. For payment, we
        are fine with cost per click, cost per impression, and/or affiliate % structures. We’re pretty flexible so just
        email us!
      </>
    ),
    header: 'I have something Commander players might like. Can I advertise on EDHREC?',
  },
]

type Props = {
  initCountry?: string
  initRegion?: string
}

const Faq = () => {
  const router = useRouter()

  return (
    <Main>
      <Container>
        <ExplicitHead
          description='Frequently Asked Questions'
          keywords='EDHREC,questions,faq,synergy,data'
          title='FAQ | EDHREC'
        />
        <SimpleHeader>Frequently Asked Questions</SimpleHeader>
        <Panel>
          <Accordion className='mb-3' defaultActiveKey={router.query?.q}>
            {DATA.map(({ body, header }, i) => (
              <Accordion.Item eventKey={`${i}`} key={i}>
                <Accordion.Header>{header}</Accordion.Header>
                <Accordion.Body>{body}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Panel>
      </Container>
    </Main>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async ({ req }) => {
  if (userIsBanned(req.headers)) return { redirect: { destination: '/access-restricted', permanent: false } }
  const props: Props = {}
  if (req.headers['cloudfront-viewer-country']) props.initCountry = req.headers['cloudfront-viewer-country'] as string
  if (req.headers['cloudfront-viewer-country-region'])
    props.initRegion = req.headers['cloudfront-viewer-country-region'] as string
  return { props }
}

export default Faq
